import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LOGO_LIGHT } from "../components/Navbar";
import { CheckCircle } from "@phosphor-icons/react";

const BUILDING = "https://images.unsplash.com/photo-1623428997047-296a86d28a20?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-navy-950 placeholder:text-gray-400 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please accept the Terms & Conditions");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data); toast.success("Account created. Welcome, Partner."); navigate("/dashboard");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy-950">
      <div className="relative hidden lg:block">
        <img src={BUILDING} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-navy-950/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-display text-3xl text-white leading-tight max-w-md">Join EBG FinCorp. Become a Franchise Partner across India's fastest-growing brands.</p>
          <ul className="mt-8 space-y-3">
            {["Access exclusive opportunities", "Track your applications", "Manage your partnerships"].map((p) => (
              <li key={p} className="flex items-center gap-3 text-ebg-muted text-sm"><CheckCircle size={18} className="text-gold" weight="fill" /> {p}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
            <img src={LOGO_LIGHT} alt="EBG Franchise Partner" className="h-9 w-auto mb-7" />
            <h1 className="font-display text-4xl text-navy-950">Create Account</h1>
            <p className="text-sm text-gray-500 mt-1.5">Already have an account? <Link to="/login" className="text-gold font-medium hover:underline">Login</Link></p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
              <div>
                <label className="text-xs font-medium text-gray-600">Full Name</label>
                <input data-testid="register-name-input" required placeholder="Enter your full name" value={form.name} onChange={set("name")} className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Mobile Number</label>
                <div className="flex gap-2 mt-1.5">
                  <span className="grid place-items-center px-3 bg-gray-50 border border-gray-200 rounded-lg text-navy-950 text-sm">+91</span>
                  <input data-testid="register-phone-input" type="tel" placeholder="Enter your mobile number" value={form.phone} onChange={set("phone")} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Email Address</label>
                <input data-testid="register-email-input" type="email" required placeholder="Enter your email address" value={form.email} onChange={set("email")} className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Password</label>
                <input data-testid="register-password-input" type="password" required minLength={6} placeholder="Create a strong password" value={form.password} onChange={set("password")} className={`${inputCls} mt-1.5`} />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="register-agree" className="mt-0.5 accent-gold" />
                I agree to the <span className="text-gold">Terms & Conditions</span> and <span className="text-gold">Privacy Policy</span>
              </label>
              <button data-testid="register-submit-btn" disabled={loading} className="w-full btn-gold py-3 rounded-lg disabled:opacity-50">{loading ? "Creating…" : "Create Account"}</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
